import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './PriceList.module.scss';
import classNames from 'classnames/bind';
import { BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/prices/menu-option/MenuOption';
import { IPrice, IPriceList } from '~/utils/interface/estimate';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { priceActions } from '~/thunks/price/pricesSlice';
import { handleGetTags } from '~/thunks/tag/tagThunk';
import { handleApiError } from '~/utils/helper';

const cx = classNames.bind(styles);

type PriceListProps = {
  data?: IPrice;
  onOpenEdit: (id: string) => void;
};

function PriceList(props: PriceListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/price']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('properties.name'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'city',
        label: t('properties.city'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'state',
        label: t('properties.state'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'zip_code',
        label: t('properties.zipCode'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'material_sale_tax',
        label: t('properties.materialSaleTax'),
        sortable: true,
        searchable: false,
      },
    ],
    [t]
  );

  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { prices, filter, currentPage, totalPages } = useAppSelector((state) => state.price);
  const dispatch = useAppDispatch();

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(priceActions.changePriceId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(priceActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    await fetchTagData();
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(priceActions.changeMenuSelectId(menuSelectedId));
      dispatch(priceActions.changeOpenAlertDelete(true));
    }
  };
  const fetchTagData = async () => {
    try {
      await dispatch(handleGetTags()).unwrap();
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      priceActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      priceActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };
  return (
    <Box className={cx('price-component-list-container')}>
      <BaseTable<IPriceList>
        data={prices}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(price: IPriceList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{price.name}</Box>
                </Box>
              </TableCell>

              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{price.city}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{price.state}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{price.zip_code}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{price.material_sale_tax}</Box>
                </Box>
              </TableCell>
              {(permissions.price_delete || permissions.price_edit) && (
                <TableCell>
                  <Box className={cx('deadline-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, price.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
      />
    </Box>
  );
}

export default memo(PriceList);
